import { Controller } from "@hotwired/stimulus"
import { groupByTab, highlightRanges, prepare, search } from "../utils/settings_search"

// The search box in the settings page header. Settings::SearchIndex renders every place this
// account may open into the entries value, so typing filters locally with no request.
//
// A result is a plain link to its tab with the card's anchor
// (/settings/timesheets#settings-card-break_rules): a Turbo Drive visit, so the dirty guard
// of an open card still asks first, and Turbo scrolls to the anchor. The box sits outside
// the settings_panel frame, so it reconnects after that visit and marks the card. A result
// on the page already open only differs by its hash, which Turbo scrolls to without a
// visit — that case is handled here.
export default class extends Controller {
  static targets = ["input", "results", "status"]
  static values = {
    entries: Array,
    startHint: String,
    noResults: String,
    matchedVia: String,
    keyboardHint: String,
    countOne: String,
    countOther: String,
  }

  // How long the jumped-to card keeps its ring.
  static FLASH_MS = 2000
  static FLASH_CLASSES = ["ring-4", "ring-green-300", "ring-offset-2", "transition-shadow"]

  // entriesValueChanged prepares the index; Stimulus calls it before connect.
  connect() {
    this.activeIndex = -1
    this.flashFromLocation()
  }

  disconnect() {
    clearTimeout(this.flashTimer)
  }

  entriesValueChanged() {
    this.prepared = prepare(this.entriesValue)
  }

  open() {
    this.render()
  }

  search() {
    this.activeIndex = 0
    this.render()
  }

  // Leaving the box closes the list; a click on a result keeps focus (keepFocus) first.
  close() {
    this.resultsTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  keepFocus(event) {
    event.preventDefault()
  }

  navigate(event) {
    const options = this.options()
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        if (this.resultsTarget.hidden) this.render()
        if (options.length === 0) return
        const step = event.key === "ArrowDown" ? 1 : -1
        this.activeIndex = (this.activeIndex + step + options.length) % options.length
        this.markActive()
        break
      }
      case "Enter": {
        const option = options[this.activeIndex] || options[0]
        if (!option) return
        event.preventDefault()
        this.follow(option)
        break
      }
      case "Escape":
        if (this.inputTarget.value === "" || this.resultsTarget.hidden) {
          this.close()
        } else {
          this.inputTarget.value = ""
          this.render()
        }
        break
    }
  }

  pick(event) {
    const option = event.target.closest('[role="option"]')
    if (!option || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return
    event.preventDefault()
    this.follow(option)
  }

  follow(option) {
    const target = new URL(option.href, window.location.href)
    this.close()
    this.inputTarget.blur()
    if (this.onPage(target)) {
      // replaceState keeps Turbo's own state on the entry: a pushed copy of it would make
      // Back start a restore visit that re-renders the page instead of just going back.
      window.history.replaceState(window.history.state, "", target.hash)
      this.flash(target.hash, { scroll: true })
    } else if (window.Turbo) {
      window.Turbo.visit(target.href)
    } else {
      window.location.href = target.href
    }
  }

  // Whether the result's card is on screen already, so scrolling is enough. The payroll and
  // custom-field sub-tabs switch on the client without touching the URL: the address can
  // match while the card sits in a hidden panel, or another sub-tab shows than the result
  // names. Every such case visits, and the server opens the sub-tab from ?subtab=.
  onPage(target) {
    if (target.pathname !== window.location.pathname || target.search !== window.location.search)
      return false
    const card = this.findCard(target.hash)
    return Boolean(card && !card.closest(".hidden, [hidden]"))
  }

  findCard(hash) {
    if (!hash) return null
    try {
      return document.querySelector(hash)
    } catch {
      return null
    }
  }

  // --- rendering ---

  render() {
    const query = this.inputTarget.value
    const list = this.resultsTarget
    list.replaceChildren()

    if (query.trim() === "") {
      list.append(this.message(this.startHintValue))
      this.show(0)
      return
    }

    const hits = search(this.prepared, query)
    if (hits.length === 0) {
      list.append(this.message(this.noResultsValue.replace("__QUERY__", query.trim())))
      this.show(0)
      return
    }

    let index = 0
    groupByTab(hits).forEach(({ tab, items }) => {
      const heading = document.createElement("div")
      heading.className =
        "px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400"
      heading.setAttribute("role", "presentation")
      heading.textContent = tab
      list.append(heading)
      items.forEach((hit) => list.append(this.option(hit, query, index++)))
    })
    const hint = this.message(this.keyboardHintValue)
    hint.classList.add("hidden", "sm:block", "border-t", "border-gray-100", "mt-1")
    list.append(hint)

    this.activeIndex = Math.min(Math.max(this.activeIndex, 0), index - 1)
    this.show(index)
    this.markActive()
  }

  option(hit, query, index) {
    const link = document.createElement("a")
    link.href = hit.entry.url
    link.id = `settings_search_option_${index}`
    link.setAttribute("role", "option")
    link.setAttribute("aria-selected", "false")
    link.dataset.turbo = "false"
    link.className =
      "block rounded-md px-3 py-2 text-sm text-gray-900 hover:bg-green-50 aria-selected:bg-green-50"

    const title = document.createElement("span")
    title.className = "block font-medium"
    this.appendHighlighted(title, hit.entry.title, query)
    link.append(title)

    const detail = [
      hit.entry.group,
      hit.via && this.matchedViaValue.replace("__KEYWORD__", hit.via),
    ]
      .filter(Boolean)
      .join(" · ")
    if (detail) {
      const line = document.createElement("span")
      line.className = "block text-xs text-gray-500"
      line.textContent = detail
      link.append(line)
    }
    return link
  }

  appendHighlighted(element, text, query) {
    let position = 0
    highlightRanges(text, query).forEach(([start, end]) => {
      if (start > position) element.append(text.slice(position, start))
      const mark = document.createElement("mark")
      mark.className = "rounded-sm bg-yellow-100 text-gray-900"
      mark.textContent = text.slice(start, end)
      element.append(mark)
      position = end
    })
    if (position < text.length) element.append(text.slice(position))
  }

  message(text) {
    const paragraph = document.createElement("p")
    paragraph.className = "px-3 py-2 text-sm text-gray-500"
    paragraph.textContent = text
    return paragraph
  }

  show(count) {
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    if (this.hasStatusTarget && this.inputTarget.value.trim() !== "") {
      const template = count === 1 ? this.countOneValue : this.countOtherValue
      this.statusTarget.textContent = template.replace("__COUNT__", count)
    }
  }

  options() {
    return [...this.resultsTarget.querySelectorAll('[role="option"]')]
  }

  markActive() {
    const options = this.options()
    options.forEach((option, index) =>
      option.setAttribute("aria-selected", String(index === this.activeIndex)),
    )
    const active = options[this.activeIndex]
    if (!active) return
    this.inputTarget.setAttribute("aria-activedescendant", active.id)
    active.scrollIntoView({ block: "nearest" })
  }

  // --- arrival ---

  // After the visit Turbo has already scrolled; a card on a sub-tab that its controller
  // reveals only on connect can still be hidden at that moment, so scroll once more.
  flashFromLocation() {
    if (
      !window.location.hash.startsWith("#settings-card-") &&
      !window.location.hash.startsWith("#billing-")
    )
      return
    setTimeout(() => this.flash(window.location.hash, { scroll: true }), 0)
  }

  flash(hash, { scroll } = {}) {
    const card = this.findCard(hash)
    if (!card) return
    if (scroll) {
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      card.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
    }
    const classes = this.constructor.FLASH_CLASSES
    card.classList.add(...classes)
    clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => card.classList.remove(...classes), this.constructor.FLASH_MS)
  }
}
